import React, { useState, useRef, useMemo, useEffect, useContext, useCallback } from "react";
import {
	Box,
	Button,
	Typography,
	CircularProgress,
	List,
	ListItem,
	ListItemButton,
	ListItemText,
	ListSubheader,
	Collapse,
	IconButton,
	Card,
	CardContent,
	Menu,
	MenuItem,
} from "@mui/material";
import { Upload, Delete, Download, ExpandMore, ExpandLess, CheckCircle, AttachFile } from "@mui/icons-material";
import { UserContext } from "../../../../../../../hooks/UserContext";
import { FeatureCollection } from "geojson";
import type { Job } from "../../../../../../../models";
import { ImportTableColumn } from "../../../../../../../utilities/types";
import {
	ValidationContext,
	isFieldRequired,
	isFieldVisible,
	getValidationErrorKey,
} from "../../../../../../../utilities/jobValidation";
import { columnDisplayNames } from "../../../../../../admin/ImportSettings";
import { GeoJSONOptionsKey, GeoJSONFileKey, GroupedGeoJSONOption } from "../../../ImportTable.types";
import { ColumnContext } from "../ColumnContext";
import { useGeoJSONFileColumnViewModel } from "./geoJSONFileColumn.vm";
import { DOWNLOAD_FORMAT_OPTIONS, GeoJSONDownloadFormat, PendingPointsUpload } from "./geoJSONFileColumn.types";
import { normalizePointsFC } from "../../../../../../../domain/sample/sampleService";
import { useValidationErrors } from "../../../shared/hooks/ValidationErrorsContext";
import { SampleIdPropertyDialog } from "../../../../../shared/SampleIdPropertyDialog";

export interface GeoJSONFileColumnMobileProps {
	/** The job being edited */
	job: Job;
	/** Column context for updates and validation */
	columnContext: ColumnContext;
	/** The options key for fileOptions */
	optionsKey: GeoJSONOptionsKey;
	/** The key for files storage */
	geoJSONKey: GeoJSONFileKey;
	/** The column identifier */
	columnId: ImportTableColumn;
	/** Custom label override */
	label?: string;
}

/**
 * Mobile-friendly GeoJSON file component for ImportTable fields.
 * Simplified version with touch-friendly upload, selection, and download.
 */
export const GeoJSONFileColumnMobile: React.FC<GeoJSONFileColumnMobileProps> = ({
	job,
	columnContext,
	optionsKey,
	geoJSONKey,
	columnId,
	label,
}) => {
	const vm = useGeoJSONFileColumnViewModel(columnContext);
	const validationErrors = useValidationErrors();
	const [isLoading, setIsLoading] = useState(false);
	const [showOptions, setShowOptions] = useState(false);
	const [downloadMenuAnchorEl, setDownloadMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [pendingUpload, setPendingUpload] = useState<PendingPointsUpload | null>(null);
	const fileInputRef = useRef<HTMLInputElement>(null);
	// Track if initial auto-select has been done to prevent re-selecting after user deletes
	const hasAutoSelectedRef = useRef(false);

	// Check if user is a superuser for format menu
	const userContext = useContext(UserContext);
	const isSuperuser = userContext?.isSuperUser ?? false;

	const errorKey = getValidationErrorKey(columnId, job.id);
	const hasError = !!validationErrors[errorKey];
	const errorMessage = validationErrors[errorKey];

	// Build validation context
	const deal = columnContext.props.allDeals?.find((d) => d.id === job.dealIds?.[0]);
	const fileContext: ValidationContext = {
		...columnContext.validationContext,
		job,
		deal,
		isPointsAttached: job.isPointsAttached,
	};
	const isRequired = isFieldRequired(columnId, fileContext);
	const isFieldHidden = !isFieldVisible(columnId, fileContext);

	// Get available options and current selection
	const availableOptions = useMemo(() => vm.getAvailableOptions(job, optionsKey), [job.fileOptions, optionsKey]);
	const currentGeoJSON = job.files?.[geoJSONKey] as FeatureCollection | undefined;
	const selectedOption = useMemo(
		() => vm.getSelectedOption(availableOptions, currentGeoJSON),
		[availableOptions, currentGeoJSON],
	);
	const groupedOptions = useMemo(() => vm.getGroupedMenuOptions(availableOptions), [availableOptions]);
	const optionCount = availableOptions.length;

	// Auto-select single option only on initial mount (not after user deletes)
	useEffect(() => {
		// Skip if we've already auto-selected for this component instance
		if (hasAutoSelectedRef.current) return;

		if (optionCount === 1 && !currentGeoJSON && !isFieldHidden) {
			hasAutoSelectedRef.current = true;
			vm.updateJobGeoJSON(job.id, geoJSONKey, availableOptions[0].option.geoJSON);
		}
	}, [optionCount, currentGeoJSON, isFieldHidden, availableOptions]);

	// Mark as auto-selected if user already has a file (prevents re-selecting after delete)
	useEffect(() => {
		if (currentGeoJSON) {
			hasAutoSelectedRef.current = true;
		}
	}, []);

	// File input reset helper
	const resetFileInput = () => {
		if (fileInputRef.current) fileInputRef.current.value = "";
	};

	const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			vm.handleUpload(job.id, optionsKey, geoJSONKey, file, setIsLoading, resetFileInput, setPendingUpload);
		}
	};

	// Sample ID dialog handlers
	const handleSampleIdConfirm = useCallback(
		(key: string) => {
			if (!pendingUpload) return;
			const normalized = normalizePointsFC(pendingUpload.geoJSON, { sampleIdKey: key });
			vm.updateJobWithUpload(
				pendingUpload.jobId,
				pendingUpload.optionsKey,
				pendingUpload.geoJSONKey,
				normalized,
				pendingUpload.fileName,
			);
			setPendingUpload(null);
		},
		[pendingUpload, vm],
	);

	const handleSampleIdSkip = useCallback(() => {
		if (!pendingUpload) return;
		const normalized = normalizePointsFC(pendingUpload.geoJSON);
		vm.updateJobWithUpload(
			pendingUpload.jobId,
			pendingUpload.optionsKey,
			pendingUpload.geoJSONKey,
			normalized,
			pendingUpload.fileName,
		);
		setPendingUpload(null);
	}, [pendingUpload, vm]);

	const handleSelectOption = (option: GroupedGeoJSONOption) => {
		vm.updateJobGeoJSON(job.id, geoJSONKey, option.option.geoJSON);
		setShowOptions(false);
	};

	const handleDelete = () => {
		if (currentGeoJSON) {
			vm.handleDelete(job.id, optionsKey, geoJSONKey, currentGeoJSON);
		}
	};

	const handleDownloadWithFormat = (format?: GeoJSONDownloadFormat) => {
		if (currentGeoJSON && selectedOption) {
			vm.handleDownload(currentGeoJSON, selectedOption, setIsLoading, format);
		}
		setDownloadMenuAnchorEl(null);
	};

	// Render download button - shows menu for superusers, simple button for others
	const renderDownloadButton = (disabled: boolean = false) => (
		<>
			{isSuperuser ? (
				<IconButton
					size="small"
					onClick={(e) => setDownloadMenuAnchorEl(e.currentTarget)}
					disabled={isLoading || disabled}
				>
					<Download fontSize="small" />
				</IconButton>
			) : (
				<IconButton size="small" onClick={() => handleDownloadWithFormat()} disabled={isLoading || disabled}>
					<Download fontSize="small" />
				</IconButton>
			)}
		</>
	);

	if (isFieldHidden) {
		return null;
	}

	const displayLabel = label || columnDisplayNames[columnId];

	return (
		<Card variant="outlined" sx={{ mb: 2 }}>
			<CardContent sx={{ p: 2, "&:last-child": { pb: 2 }, minHeight: 80 }}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
					<Typography variant="subtitle2" color={hasError ? "error" : "text.secondary"}>
						{displayLabel}
						{isRequired && " *"}
					</Typography>
					{isLoading && <CircularProgress size={20} />}
				</Box>

				{/* Hidden file input for upload */}
				<input
					ref={fileInputRef}
					type="file"
					accept=".zip,.json,.geojson,.kml,.kmz,.shp,.shx,.dbf,.prj"
					style={{ display: "none" }}
					onChange={handleFileUpload}
					disabled={isLoading}
				/>

				{/* Case 0: No options - show upload button only */}
				{optionCount === 0 && (
					<>
						{currentGeoJSON && selectedOption ? (
							<Box display="flex" alignItems="center" gap={1} mb={1}>
								<CheckCircle color="success" fontSize="small" />
								<Typography variant="body2" sx={{ flex: 1 }} noWrap>
									{selectedOption.option.name}
								</Typography>
								{renderDownloadButton()}
								<IconButton size="small" onClick={handleDelete} disabled={isLoading} color="error">
									<Delete fontSize="small" />
								</IconButton>
							</Box>
						) : (
							<Box display="flex" gap={1}>
								<Button
									variant="outlined"
									size="small"
									startIcon={<Upload />}
									onClick={() => fileInputRef.current?.click()}
									disabled={isLoading}
									sx={{ minHeight: 44 }}
								>
									Upload
								</Button>
							</Box>
						)}
					</>
				)}

				{/* Case 1: Single option - show file info with download/delete (no select menu) */}
				{optionCount === 1 && (
					<Box display="flex" alignItems="center" gap={1}>
						<CheckCircle color="success" fontSize="small" />
						<Typography variant="body2" sx={{ flex: 1 }} noWrap>
							{availableOptions[0].option.name}
						</Typography>
						{renderDownloadButton(!currentGeoJSON)}
						{currentGeoJSON && (
							<IconButton size="small" onClick={handleDelete} disabled={isLoading} color="error">
								<Delete fontSize="small" />
							</IconButton>
						)}
					</Box>
				)}

				{/* Case 2+: Multiple options - show current selection and select menu */}
				{optionCount >= 2 && (
					<>
						{/* Current selection display */}
						{currentGeoJSON && selectedOption ? (
							<Box display="flex" alignItems="center" gap={1} mb={1}>
								<CheckCircle color="success" fontSize="small" />
								<Typography variant="body2" sx={{ flex: 1 }} noWrap>
									{selectedOption.option.name}
								</Typography>
								{renderDownloadButton()}
								<IconButton size="small" onClick={handleDelete} disabled={isLoading} color="error">
									<Delete fontSize="small" />
								</IconButton>
							</Box>
						) : (
							<Typography variant="body2" color="text.secondary" mb={1}>
								No file selected
							</Typography>
						)}

						{/* Select button */}
						<Box display="flex" gap={1}>
							<Button
								variant="outlined"
								size="small"
								startIcon={<AttachFile />}
								endIcon={showOptions ? <ExpandLess /> : <ExpandMore />}
								onClick={() => setShowOptions(!showOptions)}
								sx={{ minHeight: 44 }}
							>
								Options ({optionCount})
							</Button>
						</Box>

						{/* Options list */}
						<Collapse in={showOptions}>
							<List dense disablePadding sx={{ mt: 1, bgcolor: "action.hover", borderRadius: 1, overflow: "hidden" }}>
								{Object.entries(groupedOptions).map(([groupLabel, opts]) => (
									<React.Fragment key={groupLabel}>
										<ListSubheader sx={{ bgcolor: "transparent", lineHeight: 2, pl: 2, mx: 0 }}>
											{groupLabel}
										</ListSubheader>
										{opts.map((opt) => {
											const isSelected = selectedOption?.option.name === opt.option.name;
											return (
												<ListItem key={opt.option.name} disablePadding disableGutters sx={{ mx: 0 }}>
													<ListItemButton
														onClick={() => handleSelectOption(opt)}
														selected={isSelected}
														disableGutters
														sx={{ minHeight: 44 }}
													>
														<ListItemText primary={opt.option.name} sx={{ pl: 2, pr: 2 }} />
													</ListItemButton>
												</ListItem>
											);
										})}
									</React.Fragment>
								))}
							</List>
						</Collapse>
					</>
				)}

				{/* Download format menu for superusers */}
				{isSuperuser && (
					<Menu
						anchorEl={downloadMenuAnchorEl}
						open={Boolean(downloadMenuAnchorEl)}
						onClose={() => setDownloadMenuAnchorEl(null)}
					>
						{DOWNLOAD_FORMAT_OPTIONS.map((opt) => (
							<MenuItem key={opt.value} onClick={() => handleDownloadWithFormat(opt.value)}>
								{opt.label}
							</MenuItem>
						))}
					</Menu>
				)}

				{/* Error message */}
				{hasError && (
					<Typography variant="caption" color="error" display="block" mt={1}>
						{errorMessage}
					</Typography>
				)}

				{/* Sample ID property selection dialog for points uploads */}
				{pendingUpload && (
					<SampleIdPropertyDialog
						open={true}
						propertyKeys={pendingUpload.propertyKeys}
						onConfirm={handleSampleIdConfirm}
						onSkip={handleSampleIdSkip}
					/>
				)}
			</CardContent>
		</Card>
	);
};
